import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { walterAssistantClient } from "@/services/walter-assistant";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

// Messages from CTI to CRM : https://support.diabolocom.com/en/support/solutions/articles/12000074420-standard-web-cti-general-presentation#SSO-in-window
// Messages from CRM to CTI: https://support.diabolocom.com/en/support/solutions/articles/12000079028-standard-web-cti-messages-from-crm-to-cti

type CRMPopData = { campaign_id: number | null; crm_popup_id: string | null; phone_number: string | null };
type DiabolocomCallEventData = {
    agent?: string | null;
    agentId?: number | null;
    callId?: number | string | null;
    callType?: string | null;
    campaignId?: number | null;
    contactNumber?: string | null;
    // Numéro de service du sales (celui composé pour joindre l'agent) : sert de clé
    // d'entrée au scénario Diabolocom. `displayedNumber` en repli.
    serviceNumber?: string | null;
    displayedNumber?: string | null;
    contactSessionId?: string | null;
    conversationStart?: string | null;
    conversationDuration?: number | null;
    callEnd?: string | null;
    reason?: string | null;
    customFields?: Record<string, string | null | undefined>;
    variables?: Record<string, string | null | undefined>;
    crmPopupData?: CRMPopData;
    [key: string]: unknown;
};

// `callEnd` reason émis par Diabolocom quand le sales confirme un transfert :
// c'est notre signal pour pousser le contexte d'appel vers DynamoDB.
const TRANSFER_CONFIRM_REASON = "callFinished.transferConfirm";

type PendingCallContext = {
    callId: number | string | null;
    contactNumber: string | null;
    crm_popup_id: string | null;
    campaign_id: number | null;
    record_id: string | null;
    record_type: string | null;
};

function extractCrmPopupId(data: DiabolocomCallEventData): string | null {
    return data.customFields?.["crm-popup-id"] || data.variables?.["salesforceRecordId"] || data.crmPopupData?.crm_popup_id || null;
}

type RecordType = "opportunity" | "lead" | "account" | "case";

const VALID_RECORD_TYPES = new Set<RecordType>(["opportunity", "lead", "account", "case"]);

function asRecordType(value: string | null | undefined): RecordType | null {
    if (!value) return null;
    const normalized = value.toLowerCase();
    return VALID_RECORD_TYPES.has(normalized as RecordType) ? (normalized as RecordType) : null;
}

function inferRecordType(sfId: string | null): RecordType | null {
    if (!sfId) return null;
    const prefix = sfId.slice(0, 3);
    if (prefix === "006") return "opportunity";
    if (prefix === "500") return "case";
    if (prefix === "001") return "account";
    if (prefix === "00Q") return "lead";
    return null;
}

function captureCallContext(data: DiabolocomCallEventData, campaignId: number | null = null): PendingCallContext {
    return {
        callId: data.callId ?? null,
        contactNumber: data.contactNumber ?? null,
        crm_popup_id: extractCrmPopupId(data),
        campaign_id: campaignId,
        record_id: null,
        record_type: null,
    };
}

function mergeCallContext(context: PendingCallContext | null, data: DiabolocomCallEventData): DiabolocomCallEventData {
    if (!context) return data;

    const crm_popup_id = extractCrmPopupId(data) || context.crm_popup_id;
    return {
        ...data,
        callId: data.callId ?? context.callId,
        contactNumber: data.contactNumber ?? context.contactNumber,
        crmPopupData: {
            campaign_id: context.campaign_id,
            crm_popup_id,
            phone_number: data.contactNumber ?? context.contactNumber
        }
    };
}

type DiaboloContextType = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    status: "Logout" | "Break" | "Initialisation" | "Available" | "Déjeuner" | "En formation" | "Pause";
    dialNumber: (phoneNumber: string, recordId?: string | null, recordType?: string | null) => void;
    crmPopupData?: CRMPopData;
};

const DiaboloContext = React.createContext<DiaboloContextType>(null!);

function formatPhoneNumber(number: string | null) {
    // format phone to diabolocom requirements
    // return phone string with length 0, 11 or 12 (for DOM-TOM)
    if (number) {
        let num = String(number).replace(/ /g, "").replace(/\+/g, "");
        if (num.startsWith("0")) {
            num = "33" + num.slice(-9);
        }
        // Dom-Tom management
        // Diabolocom requires a custom indicator for Dom-Tom
        if (num.startsWith("33690") || num.startsWith("33691")) {
            num = "590" + num.slice(-9); // Guadeloupe
        } else if (num.startsWith("33692") || num.startsWith("33693") || num.startsWith("33639")) {
            num = "262" + num.slice(-9); // Reunion
        } else if (num.startsWith("33694")) {
            num = "594" + num.slice(-9); // Guyanne
        } else if (num.startsWith("33696") || num.startsWith("33697")) {
            num = "596" + num.slice(-9); // Martinique
        }
        return num;
    }
    return "";
}

export function DiabolocomProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<DiaboloContextType["status"]>("Logout");
    const [incall, setIncall] = useState(false);
    const [crmPopupData, setCrmPopupData] = useState<CRMPopData>();
    const incallRef = useRef(false);
    const pendingCallContextRef = useRef<PendingCallContext | null>(null);
    const answeredCallIdsRef = useRef<Set<string>>(new Set());
    const coachingFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearCoachingFallbackTimer = () => {
        if (coachingFallbackTimerRef.current) {
            clearTimeout(coachingFallbackTimerRef.current);
            coachingFallbackTimerRef.current = null;
        }
    };

    const scheduleCoachingFallback = (callData: DiabolocomCallEventData, sourceEvent: string) => {
        clearCoachingFallbackTimer();
        coachingFallbackTimerRef.current = setTimeout(() => {
            postCoachingCallEvent("call.answered", callData, callData.conversationStart, sourceEvent);
            coachingFallbackTimerRef.current = null;
        }, 2000);
    };

    const postCoachingCallEvent = (eventType: "call.answered" | "call.ended", data: DiabolocomCallEventData, occurredAt: string | null | undefined, sourceEvent: string) => {
        const enrichedData = mergeCallContext(pendingCallContextRef.current, data);
        if (!user?.walter_assistant_coach || !user.email || !enrichedData.callId) return;

        const context = pendingCallContextRef.current;
        const voipCallId = String(enrichedData.callId);
        if (eventType === "call.answered") {
            if (answeredCallIdsRef.current.has(voipCallId)) return;
            answeredCallIdsRef.current.add(voipCallId);
            clearCoachingFallbackTimer();
        } else {
            answeredCallIdsRef.current.delete(voipCallId);
            pendingCallContextRef.current = null;
        }

        const recordId = context?.record_id || enrichedData.crmPopupData?.crm_popup_id || null;
        const recordType =
            asRecordType(context?.record_type) ||
            inferRecordType(enrichedData.crmPopupData?.crm_popup_id ?? null) ||
            null;
        walterAssistantClient
            .postCallEvent({
                event_type: eventType,
                voip_provider: "diabolocom",
                voip_call_id: voipCallId,
                agent_first_name: user.sf_user?.first_name || null,
                agent_last_name: user.sf_user?.last_name || null,
                agent_process: user.sf_user?.sales_process || null,
                agent_email: user.email,
                record_id: recordId,
                record_type: recordType,
                prospect_phone: enrichedData.contactNumber || "",
                occurred_at: occurredAt || new Date().toISOString(),
                metadata: {
                    ...enrichedData,
                    source_event: sourceEvent
                }
            })
            .catch(error => {
                console.error("Diabolocom coaching call event error:", error);
            });
    };

    // Sur confirmation de transfert, on persiste le contexte d'appel dans DynamoDB
    // (via le CRM), indexé par le numéro de service du sales. Le scénario Diabolocom
    // relira ce contexte pour retrouver le numéro du prospect à passer à l'agent IA.
    const postTransferCallContext = (data: DiabolocomCallEventData) => {
        const serviceNumber = data.serviceNumber || data.displayedNumber || null;
        const callId = data.callId != null ? String(data.callId) : null;
        if (!serviceNumber || !callId) return;

        const context = pendingCallContextRef.current;
        const crm_popup_id = extractCrmPopupId(data) || context?.crm_popup_id || null;
        const record_type = asRecordType(context?.record_type) || inferRecordType(crm_popup_id);

        DataService.client
            .url("/voice/call_context/")
            .put({
                call_id: callId,
                call_provider: "diabolocom",
                service_number: serviceNumber,
                contact_phone: data.contactNumber || context?.contactNumber || null,
                metadata: {
                    record_id: context?.record_id || crm_popup_id,
                    record_type,
                    crm_popup_id,
                    agent: data.agent ?? null,
                    agent_id: data.agentId ?? null,
                    campaign_id: context?.campaign_id ?? (typeof data.campaignId === "number" ? data.campaignId : null),
                    contact_session_id: (data.contactSessionId as string | null) ?? null
                }
            })
            .res()
            .catch(error => {
                console.error("Diabolocom call context upsert error:", error);
            });
    };

    const handleMessage = (event: MessageEvent<any>) => {
        const authorizedURIs = ["http://localhost:5173", "https://fr10.engage.diabolocom.com", "https://crm.walter-learning.com", "https://crm-stg.walter-learning.com"];
        if (!authorizedURIs.includes(event.origin)) {
            return;
        }

        console.log(`☎️ Diabolocom: ${event.data.event}`, event.data.data);

        switch (event.data.event) {
            case "agentLogin":
                setStatus("Initialisation");
                break;
            case "agentStatusChanged":
                setStatus(event.data.data.currentStatus);
                break;
            case "wrapup":
                setIncall(false);
                clearCoachingFallbackTimer();
                // Si `wrapup` précède `callEnd` sur un transfert, on persiste le contexte
                // avant de réinitialiser `pendingCallContextRef` pour ne pas le perdre.
                if (event.data.data?.reason === TRANSFER_CONFIRM_REASON) {
                    postTransferCallContext(event.data.data || {});
                }
                pendingCallContextRef.current = null;
                setCrmPopupData(undefined);
                toast.info(`Appel terminé - ${event.data.data.wrapupLabel}`);
                break;
            case "manualOutboundCall": {
                setOpen(true);
                setIncall(true);
                const newContext = captureCallContext(event.data.data || {});
                pendingCallContextRef.current = {
                    ...newContext,
                    crm_popup_id: newContext.crm_popup_id || pendingCallContextRef.current?.crm_popup_id || null,
                    campaign_id: newContext.campaign_id || pendingCallContextRef.current?.campaign_id || null,
                    record_id: pendingCallContextRef.current?.record_id || null,
                    record_type: pendingCallContextRef.current?.record_type || null,
                };
                break;
            }
            case "conversationStart":
                setOpen(true);
                setIncall(true);
                postCoachingCallEvent("call.answered", event.data.data || {}, event.data.data?.conversationStart, "conversationStart");
                break;
            case "callEnd":
                setIncall(false);
                clearCoachingFallbackTimer();
                // Avant `postCoachingCallEvent` qui réinitialise `pendingCallContextRef`.
                if (event.data.data?.reason === TRANSFER_CONFIRM_REASON) {
                    postTransferCallContext(event.data.data || {});
                }
                postCoachingCallEvent("call.ended", event.data.data || {}, event.data.data?.callEnd, "callEnd");
                setCrmPopupData(undefined);
                break;
            case "campaignOutboundCall":
            case "inboundCall": {
                setOpen(true);
                setIncall(true);
                const callData = (event.data.data || {}) as DiabolocomCallEventData;
                try {
                    const campaign_id = (callData.campaignId as number | null | undefined) ?? null;
                    const phone_number = callData.contactNumber || null;
                    const crm_popup_id = extractCrmPopupId(callData);
                    const newContext = captureCallContext(callData, campaign_id);
                    /*
                     * Préserve le contexte CRM posé par click2call (record_id =
                     * PK local + record_type) : en mode campagne Diabolocom
                     * écrase sinon avec le sf_id de la liste de preview.
                     */
                    pendingCallContextRef.current = {
                        ...newContext,
                        crm_popup_id: newContext.crm_popup_id || pendingCallContextRef.current?.crm_popup_id || null,
                        record_id: pendingCallContextRef.current?.record_id || null,
                        record_type: pendingCallContextRef.current?.record_type || null,
                    };
                    setCrmPopupData({ campaign_id, phone_number, crm_popup_id });
                } catch (e) {
                    console.error(e);
                }
                // Certains appels campagne/inbound n'émettent pas `conversationStart` séparément :
                // on déclenche le coach dès que la conversation est déjà démarrée,
                // sinon on retente après un court délai si l'événement n'arrive pas.
                if (callData.conversationStart) {
                    postCoachingCallEvent("call.answered", callData, callData.conversationStart, event.data.event);
                } else if (callData.callId) {
                    scheduleCoachingFallback(callData, event.data.event);
                }
                break;
            }
            default:
                break;
        }
    };

    // On garde une ref toujours à jour vers `handleMessage` pour que le listener
    // installé une seule fois sur `window` invoque la dernière version du
    // handler (closure fraîche sur `user`, états, etc.). On évite ainsi
    // `window.onmessage = ...` qui n'autorise qu'un seul handler et peut être
    // écrasé par n'importe quel autre script (extension, lib tierce…).
    const handleMessageRef = useRef(handleMessage);
    handleMessageRef.current = handleMessage;

    useEffect(() => {
        incallRef.current = incall;
    }, [incall]);

    useEffect(() => {
        const listener = (event: MessageEvent<any>) => handleMessageRef.current(event);
        window.addEventListener("message", listener);
        const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
            if (incallRef.current) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", beforeUnloadHandler);
        return () => {
            window.removeEventListener("message", listener);
            window.removeEventListener("beforeunload", beforeUnloadHandler);
            clearCoachingFallbackTimer();
        };
    }, []);

    const dialNumber = (phoneNumber: string, recordId?: string | null, recordType?: string | null, retry: boolean = true) => {
        if (recordId && recordType) {
            pendingCallContextRef.current = {
                callId: null,
                contactNumber: phoneNumber,
                crm_popup_id: null,
                campaign_id: null,
                record_id: recordId,
                record_type: recordType,
            };
        }
        setOpen(true);
        const diabolocomIframe = document.getElementById("diabolocom-cti-iframe") as HTMLIFrameElement;
        if (diabolocomIframe && diabolocomIframe.contentWindow) {
            diabolocomIframe.contentWindow.postMessage({ event: "dialNumber", data: { destinationNumber: formatPhoneNumber(phoneNumber) } }, "*");
        } else {
            if (retry) {
                setTimeout(() => dialNumber(phoneNumber, recordId, recordType, false), 1000);
            } else {
                console.error("Diabolocom iframe not available.");
                toast.error("Une erreur est survenue, veuillez réessayer");
            }
        }
    };

    const value = { status, dialNumber, open, setOpen, crmPopupData };
    return <DiaboloContext.Provider value={value}>{children}</DiaboloContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDiabolo() {
    return useContext(DiaboloContext);
}
